<script setup>
import { router } from '@inertiajs/vue3';
import { computed, getCurrentInstance } from 'vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import ConfirmDialog from 'primevue/confirmdialog';
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'primevue/usetoast';
import AdminMainLayout from '@/layout/admin/AdminMainLayout.vue';
import { useLoading } from '@/composables/useLoading';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const confirm = useConfirm();
const toast = useToast();
const { showLoading, hideLoading } = useLoading();

const props = defineProps({
    title: String,
    exchanges: Object,
    globalFixedAdditions: Object,
});

const exchangesData = computed(() => {
    return props.exchanges.data || [];
});

const destroy = (exchange) => {
    confirm.require({
        message: `${$t('Are you sure you want to delete')} ${exchange.currency}?`,
        header: $t('Delete Confirmation'),
        icon: 'pi pi-exclamation-triangle',
        rejectClass: 'p-button-secondary p-button-outlined',
        acceptClass: 'p-button-danger',
        accept: () => {
            router.delete(route('exchanges.destroy', exchange.id), {
                preserveScroll: true,
                preserveState: true,
                onStart: () => showLoading('Deleting exchange...'),
                onFinish: () => hideLoading(),
                onSuccess: () => {
                    toast.add({ severity: 'success', summary: 'Success', detail: 'Exchange deleted successfully', life: 3000 });
                },
                onError: () => {
                    toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete exchange', life: 3000 });
                }
            });
        }
    });
};

const onPage = (event) => {
    const page = event.page + 1;
    router.get(route('exchanges.index'), { page }, {
        preserveState: true,
        preserveScroll: true,
        onStart: () => showLoading('Loading page...'),
        onFinish: () => hideLoading()
    });
};

const formatValue = (value) => {
    return (value / 100).toFixed(2);
};

const getSuggestedFormula = (exchange) => {
    const globalFixed = props.globalFixedAdditions?.[exchange.currency] || 0;
    const fixed = globalFixed / 100;
    const percentage = exchange.percentage_addition ? (exchange.percentage_addition / 10000) + 1 : 1;
    const percentFormatted = percentage.toFixed(2);

    if (exchange.apply_percentage_to_total) {
        // Type C = (price + fixed) * %
        return `= (PRICE + ${fixed}) * ${percentFormatted}`;
    } else {
        // Type B = (price * %) + fixed (or Type A if no percentage)
        return `= (PRICE * ${percentFormatted}) + ${fixed}`;
    }
};

const paginatorTemplate = {
    layout: 'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport',
    CurrentPageReport: 'Showing {first} to {last} of {totalRecords} entries'
};
</script>

<template>
    <AdminMainLayout>
        <ConfirmDialog />

        <div class="flex justify-between items-center mb-2">
            <h2 class="text-2xl font-semibold text-gray-900">{{ $t('Exchange Rates') }}</h2>
            <div class="flex gap-3">
                <Button :label="$t('Manage Global Fixed Addition')" icon="pi pi-cog" severity="info" outlined
                    @click="() => router.visit(route('admin.formula-tax'), {
                        onStart: () => showLoading('Loading global settings...'),
                        onFinish: () => hideLoading()
                    })" />
                <Button :label="$t('Add Exchange')" icon="pi pi-plus" class="p-button-success"
                    @click="() => router.visit(route('exchanges.create'), {
                        onStart: () => showLoading('Loading create form...'),
                        onFinish: () => hideLoading()
                    })" />
            </div>
        </div>
        <p class="mb-4">{{ $t('All currency calculations are based on the US dollar.') }}</p>

        <DataTable
            :value="exchangesData"
            :paginator="true"
            :rows="15"
            :totalRecords="exchanges.total"
            :lazy="true"
            @page="onPage"
            dataKey="id"
            responsiveLayout="scroll"
            :paginatorTemplate="paginatorTemplate.layout"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
            class="p-datatable-sm">

            <template #empty>
                <div class="text-center py-4">{{ $t('No exchange rates found.') }}</div>
            </template>

            <Column field="currency" :header="$t('Currency')" :sortable="true" style="min-width: 200px">
                <template #body="slotProps">
                    <span class="text-blue-600 hover:text-blue-800 font-medium cursor-pointer"
                        @click="() => router.visit(route('exchanges.edit', slotProps.data.id), {
                            onStart: () => showLoading('Loading exchange...'),
                            onFinish: () => hideLoading()
                        })">
                        {{ slotProps.data.currency }}
                    </span>
                </template>
            </Column>

            <Column field="value" :header="$t('Exchange')" :sortable="true" style="min-width: 150px">
                <template #body="slotProps">
                    <Tag :value="formatValue(slotProps.data.value)" severity="info" />
                </template>
            </Column>

            <Column field="suggested_form" :header="$t('Suggested Price Formula')" style="min-width: 250px">
                <template #body="slotProps">
                    <Tag :value="getSuggestedFormula(slotProps.data)" severity="warning" />
                </template>
            </Column>

            <Column :header="$t('Actions')" :exportable="false" style="min-width: 150px">
                <template #body="slotProps">
                    <div class="flex gap-2 min-h-[40px]">
                        <Button
                            v-if="slotProps.data.currency !== 'USD'"
                            icon="pi pi-pencil"
                            class="p-button-rounded p-button-text p-button-info"
                            v-tooltip.top="$t('Edit')"
                            @click="() => router.visit(route('exchanges.edit', slotProps.data.id), {
                                onStart: () => showLoading('Loading exchange...'),
                                onFinish: () => hideLoading()
                            })" />
                        <Button
                            v-if="slotProps.data.currency !== 'USD'"
                            icon="pi pi-trash"
                            class="p-button-rounded p-button-text p-button-danger"
                            v-tooltip.top="$t('Delete')"
                            @click="destroy(slotProps.data)" />
                    </div>
                </template>
            </Column>
        </DataTable>
    </AdminMainLayout>
</template>

<style scoped>
.card {
    background: white;
    padding: 1.5rem;
    border-radius: 0.5rem;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
}
</style>
